import React from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { CustomerStoryStats } from "@/content/customerStories/customerStories.types";
import { wacToken } from "@/libraries/design/foundations";

type Nullable<T> = T | null;

export interface CustomerDetailsStatProps {
    title?: string;
    stats?: Nullable<CustomerStoryStats[]>;
}

// Stats become vertical when the viewport is tablet or smaller

const containerStyle = css({
    borderRadius: wacToken("corner.radius.card"),
    alignSelf: "center",
    background: wacToken("color.neutral.light100"),
    paddingInline: token("space.400"),
    display: "flex",
    flexDirection: "column",
    gap: token("space.300"),
    paddingBlock: token("space.400"),
    [media.below.md]: {
        paddingInline: token("space.300"),
        gap: token("space.200")
    }
});

const horizontalContainerWithThreeStatsStyle = css({
    [media.above.md]: {
        width:
            "calc(100% + " +
            token("space.400") +
            "+ " +
            token("space.400") +
            ")",
        marginBlock: token("space.0"),
        marginInline: token("space.negative.400")
    }
});

const statsContainerStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: token("space.400"),
    [media.below.md]: {
        gap: token("space.300")
    },
    [media.below.sm]: {
        flexDirection: "column",
        gap: token("space.200")
    }
});

const statStyle = css({
    display: "flex",
    alignSelf: "stretch",
    flexGrow: 1,
    flexBasis: 0,
    flexDirection: "column"
});

const statTextStyle = css({
    alignSelf: "stretch",
    margin: 0
});

const horizontalStatStyle = css({
    [media.above.md]: {
        flexDirection: "row",
        gap: token("space.400")
    }
});

const horizontalStatTextStyle = css({
    [media.above.md]: {
        alignSelf: "center"
    }
});

const layoutStyle = css({
    display: "flex",
    justifyContent: "center",
    width: "100%",
    alignSelf: "center"
});

const oneStatDesktopStyle = css({
    [media.above.md]: {
        width: "75%",
        marginInline: "auto"
    }
});

// Stat number color to be changed in the future to be dynamic based on the page-level accent color

const isEmptyStats = (currentObject: CustomerStoryStats) =>
    JSON.stringify(currentObject) === "{}";

export const CustomerDetailsStats = ({
    title,
    stats
}: CustomerDetailsStatProps) => {
    if (!stats || stats?.every(isEmptyStats)) {
        return null;
    }
    return (
        <div css={layoutStyle}>
            <div
                css={[
                    containerStyle,
                    stats?.length === 1 && oneStatDesktopStyle,
                    stats?.length === 3 &&
                        horizontalContainerWithThreeStatsStyle
                ]}
            >
                <div className="eyebrow-md" role="heading" aria-level={2}>
                    {title}
                </div>
                <div css={statsContainerStyle}>
                    {stats?.map((stat, idx) => {
                        return (
                            <React.Fragment key={idx}>
                                <div
                                    css={[
                                        statStyle,
                                        stats?.length < 3 && horizontalStatStyle
                                    ]}
                                >
                                    <p
                                        className="stat-sm"
                                        css={[
                                            statTextStyle,
                                            stats?.length < 3 &&
                                                horizontalStatTextStyle
                                        ]}
                                    >
                                        {stat?.stat}
                                    </p>
                                    <p
                                        className="body-md"
                                        css={[
                                            statTextStyle,
                                            stats?.length < 3 &&
                                                horizontalStatTextStyle
                                        ]}
                                    >
                                        {stat?.statDescription}
                                    </p>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
